import { ChatSettingsFormData } from '@choco-admin/types';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { AlertCard } from '../../../../../../../components/AlertCard';
import { useCustomerNumberUsage } from '../../../../../hooks/useCustomerNumberUsage';

interface CustomerNumberWarningProps {
  chatId: string;
  supplierEntityId: string | null | undefined;
  savedCustomerNumber: string | null | undefined;
}

const formatNames = (names: string[]): string =>
  names.length <= 2
    ? names.join(' and ')
    : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;

export const CustomerNumberWarning: React.FC<CustomerNumberWarningProps> = ({
  chatId,
  supplierEntityId,
  savedCustomerNumber,
}) => {
  const { watch } = useFormContext<ChatSettingsFormData>();
  const customerNumber = (watch('customerNumber') ?? '').trim();
  const saved = (savedCustomerNumber ?? '').trim();

  const { otherChats } = useCustomerNumberUsage({
    supplierEntityId,
    customerNumber,
    chatId,
  });

  const isChangingSavedNumber =
    saved !== '' && customerNumber !== '' && customerNumber !== saved;

  if (!isChangingSavedNumber && otherChats.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="customer-number-warning">
      {isChangingSavedNumber && (
        <AlertCard
          type="warning"
          title="Customer number changed"
          message={`The distributor receives this customer's orders under this number. Only change it from ${saved} if the distributor gave you the new number.`}
        />
      )}
      {otherChats.length > 0 && (
        <AlertCard
          type="warning"
          title="Number already in use"
          message={`${formatNames(otherChats.map((chat) => chat.name))} already ${otherChats.length === 1 ? 'uses' : 'use'} customer number ${customerNumber} at this distributor. Check the number with the distributor before saving.`}
        />
      )}
    </div>
  );
};
